import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material'
import { cs, enUS } from 'date-fns/locale'

import { searchCustomers } from '../../service/customers.service'
import { CustomerSearchResultDto } from '../../service/types'
import { formatLastLesson } from '../../utils/formatLastLesson'

export const CustomerSearch: FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [options, setOptions] = useState<CustomerSearchResultDto[]>([])
  const [inputValue, setInputValue] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dateLocale = i18n.language === 'cs' ? cs : enUS

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const doSearch = useCallback(async (query: string) => {
    if (query.length < 1) {
      setOptions([])
      setHasSearched(false)
      return
    }
    try {
      const response = await searchCustomers(query)
      setOptions(response.data)
      setHasSearched(true)
    } catch {
      setOptions([])
      setHasSearched(true)
    }
  }, [])

  const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    setInputValue(value)
    if (reason === 'input') {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => doSearch(value), 300)
    }
  }

  const handleChange = (_event: React.SyntheticEvent, value: CustomerSearchResultDto | null) => {
    if (value) {
      navigate(`/customer/${value.id}`)
      setInputValue('')
      setOptions([])
      setHasSearched(false)
    }
  }

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: CustomerSearchResultDto) => {
    const details = [option.phone, option.email].filter(Boolean).join(' \u00B7 ')
    return (
      <li {...props} key={option.id}>
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {option.name}
          </Typography>
          {details && (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {details}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.3 }}>
            {formatLastLesson(option.lastLessonDate, t, dateLocale)}
          </Typography>
        </Box>
      </li>
    )
  }

  return (
    <Autocomplete
      open={hasSearched}
      options={options}
      value={null}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={() => {
        setHasSearched(false)
      }}
      getOptionLabel={(option) => option.name}
      renderOption={renderOption}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(opts) => opts}
      popupIcon={null}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={t('header.searchPlaceholder')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 1 }}>
                <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(138,52,138,0.04)',
              borderRadius: '50px',
              height: 42,
              '& fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
              '&:hover fieldset': { borderColor: 'primary.dark', borderWidth: 2 },
              '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 2 },
            },
          }}
        />
      )}
      sx={{ flex: 1, maxWidth: 500, mx: 2 }}
      noOptionsText={hasSearched ? t('header.noResults') : ''}
      clearOnBlur
    />
  )
}
